import { Close } from '@mui/icons-material'
import { ExpandMoreRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Stack, Typography } from '@mui/material'
import React from 'react'
import { colors, theme } from '../theme'
import { StripeConnectButton } from './StripeConnectButton'
import paymentsFlexImg from '../Payments/assets/payments-flex.svg'
import paymentsFastImg from '../Payments/assets/payments-fast.svg'
import paymentsTrackImg from '../Payments/assets/payments-track.svg'

interface Props {
  isAdmin: boolean
  onClose?: () => void
  onRefresh?: () => void
}

const SectionTitle: React.FC<{ index: number; title: string }> = ({ index, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        border: `3px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 13,
        flexShrink: 0,
      }}
    >
      {index}
    </Box>
    <Typography sx={{ fontWeight: 800, color: colors.cello, fontSize: 16, lineHeight: 1.2 }}>{title}</Typography>
  </Box>
)

const REFRESH_KEY = 'stripe-connect-attempted'

export const StripeConnectRequiredStep: React.FC<Props> = ({ isAdmin, onClose, onRefresh }) => {
  const [hasTriedConnect, setHasTriedConnect] = React.useState<boolean>(
    () => window.sessionStorage.getItem(REFRESH_KEY) === 'true'
  )

  const handleConnectAttempt = () => {
    setHasTriedConnect(true)
    window.sessionStorage.setItem(REFRESH_KEY, 'true')
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: '1000px',
        width: { xs: '100%', md: '90%' },
        mx: 'auto',
        position: 'relative',
      }}
    >
      {onClose && (
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <Close />
        </IconButton>
      )}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 800, color: colors.blueSky, fontSize: { xs: 22, md: 28 } }}>
          {isAdmin ? '🐶 Action requise' : "🐶 Action d'un administrateur requise"}
        </Typography>
        <Typography sx={{ fontWeight: 700, color: colors.blueSky, fontSize: { xs: 18, md: 24 } }}>
          Activer le paiement adoptant en ligne
        </Typography>
      </Box>

      {/* Intro content (same idea as "Reception des frais d'adoption avec Petso") */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 1.5, md: 2 },
          borderRadius: 4,
          border: `1px solid ${colors.geyser}`,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
        }}
      >
        <Stack spacing={0.6} sx={{ alignItems: 'center', textAlign: 'center', mb: 2 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#7A8197' }}>Gratuit</Typography>
          <Typography sx={{ fontSize: { xs: '1.25rem', md: '1.45rem' }, fontWeight: 900, color: '#171F34' }}>
            Faites-vous payer grâce à un lien.
          </Typography>
          <Typography sx={{ fontSize: { xs: '0.95rem', md: '1.02rem' }, fontWeight: 800, color: '#171F34' }}>
            0% de commission à la charge.
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            // 3 columns as soon as we have enough width; stacks on mobile.
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
            textAlign: 'center',
          }}
        >
          <Box>
            <Box
              component="img"
              alt=""
              src={paymentsFlexImg}
              sx={{
                height: { xs: 150, sm: 140 },
                width: '100%',
                objectFit: 'cover',
                borderRadius: 4,
                display: 'block',
              }}
            />
            <Typography sx={{ mt: 1.25, fontSize: '1.05rem', fontWeight: 900, color: '#171F34' }}>
              Offrez plus de flexibilité
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: '0.9rem', color: '#4B5566' }}>
              Choisissez comment vos adoptants peuvent régler : paiement en une ou plusieurs fois.
            </Typography>
          </Box>

          <Box>
            <Box
              component="img"
              alt=""
              src={paymentsFastImg}
              sx={{
                height: { xs: 150, sm: 140 },
                width: '100%',
                objectFit: 'cover',
                borderRadius: 4,
                display: 'block',
              }}
            />
            <Typography sx={{ mt: 1.25, fontSize: '1.05rem', fontWeight: 900, color: '#171F34' }}>
              Faites-vous payer plus vite
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: '0.9rem', color: '#4B5566' }}>
              Plus besoin d&apos;attendre un chèque ou un virement. Réception rapide et centralisée.
            </Typography>
          </Box>

          <Box>
            <Box
              component="img"
              alt=""
              src={paymentsTrackImg}
              sx={{
                height: { xs: 150, sm: 140 },
                width: '100%',
                objectFit: 'cover',
                borderRadius: 4,
                display: 'block',
              }}
            />
            <Typography sx={{ mt: 1.25, fontSize: '1.05rem', fontWeight: 900, color: '#171F34' }}>
              Suivez les paiements
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: '0.9rem', color: '#4B5566' }}>
              Ne courez plus après vos adoptants, suivez les paiements.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Primary actions (kept close together + right after the intro block) */}
      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
        <Typography sx={{ color: colors.blueSky, fontSize: { xs: 13, md: 14 }, textAlign: 'center', maxWidth: 720 }}>
          Pour finaliser la configuration, munissez-vous de votre IBAN : il permet à Stripe de vous reverser simplement
          les frais d&apos;adoption reçus.
        </Typography>
        <StripeConnectButton
          variant="contained"
          color="primary"
          fullWidth={false}
          content="Continuer"
          onConnectAttempt={handleConnectAttempt}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            px: 6,
            py: 1.5,
            fontWeight: 700,
            backgroundColor: theme.palette.primary.main,
            color: colors.white,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        />

        {onRefresh && (hasTriedConnect || !isAdmin) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ color: colors.blueSky, fontSize: { xs: 12, md: 13 }, fontWeight: 600 }}>
              Déjà fait&nbsp;?
            </Typography>
            <LoadingButton
              variant="outlined"
              onClick={onRefresh}
              sx={{
                textTransform: 'none',
                borderRadius: '999px',
                px: 6,
                py: 1.5,
                fontWeight: 700,
                color: colors.cello,
                borderColor: colors.cello,
              }}
            >
              Rafraîchir le statut
            </LoadingButton>
          </Box>
        )}

        {!isAdmin && (
          <Typography sx={{ mt: 0.5, color: colors.blueSky, fontSize: { xs: 13, md: 14 }, textAlign: 'center' }}>
            Demandez &agrave; un administrateur de votre association d&apos;activer Stripe Connect pour continuer.
          </Typography>
        )}
      </Box>

      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 3,
          border: '1px solid #F0DE9D',
          backgroundColor: '#FFF5D6',
          overflow: 'hidden',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreRounded />}
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 0.75,
            backgroundColor: '#FFF5D6',
            '& .MuiAccordionSummary-content': { my: 0.75 },
          }}
        >
          <Typography sx={{ fontWeight: 900, color: '#171F34' }}>Comment ça marche ?</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1.5, md: 2 }, pb: 2, backgroundColor: 'white' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: { xs: 2.25, md: 3 },
            }}
          >
            <Box>
              <SectionTitle index={1} title="Activer votre compte Stripe connect x Petso" />
              <Typography sx={{ color: colors.blueSky, mb: 0, fontSize: { xs: 13, md: 14 } }}>
                Stripe est un partenaire technique spécialisé et agréé pour assurer la gestion des paiements et
                permettre la réception des frais d&apos;adoption en ligne de manière fiable et sécurisée.
              </Typography>
            </Box>

            <Box>
              <SectionTitle index={2} title="Comment ?" />
              <Typography sx={{ color: colors.blueSky, mb: 0.6, fontSize: { xs: 13, md: 14 } }}>
                A la création d&apos;une procédure d&apos;adoption :
              </Typography>
              <Box component="ul" sx={{ pl: 3, color: colors.blueSky, mb: 0, fontSize: { xs: 13, md: 14 } }}>
                <Box component="li" sx={{ mb: 0.75 }}>
                  Un mail est envoyé à l&apos;adoptant pour lui permettre de régler les frais d&apos;adoption.
                </Box>
                <Box component="li">Vous êtes notifié à chaque paiement et les suivez depuis votre espace Petso.</Box>
              </Box>
            </Box>

            <Box>
              <SectionTitle index={3} title="C'est important pour nous 😺" />
              <Typography sx={{ color: colors.blueSky, fontSize: { xs: 13, md: 14 } }}>
                <Box component="span" sx={{ fontWeight: 800 }}>
                  Lors du paiement, l&apos;adoptant peut laisser un pourboire facultatif à Petso.
                </Box>{' '}
                Ces pourboires nous permettent de couvrir les frais de paiement et la maintenance, pour continuer à
                proposer cet outil accessible aux associations !
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
